import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Form, Button, Alert } from 'react-bootstrap';
import { jwtDecode } from 'jwt-decode';
import 'bootstrap/dist/css/bootstrap.min.css';
import { API_BASE } from '../utils/api';

// ✅ Configurações Dinâmicas (SaaS Ready)
const CLIENT_NAME = process.env.REACT_APP_CLIENT_NAME || "Gerenciador de Performance";
const CLIENT_LOGO = process.env.REACT_APP_LOGO_MAIN || "/client_assets/logo-main.png";
const LOGIN_BG = process.env.REACT_APP_LOGIN_BG || "/client_assets/login-bg.png";

const Login = () => {
  const { login, isDemo, token } = useAuth();
  const { register, handleSubmit, formState: { errors } } = useForm();
  const navigate = useNavigate();
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  React.useEffect(() => {
    if (token) {
      navigate('/dashboard');
    }
  }, [token, navigate]);

  const onSubmit = async (data) => {
    try {
      if (isDemo) {
        login('token_demo_123');
        navigate('/dashboard');
        return;
      }

      const payload = {
        nome: (data?.nome || '').trim().toUpperCase(),
        senha: data?.senha || ''
      };

      // ✅ AGORA DINÂMICO: Aponta para o Backend real (localhost:5000 no seu PC)
      const response = await fetch(`${API_BASE}/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      const result = await response.json();

      if (response.ok) {
        const { token, refresh_token } = result;
        login(token, refresh_token);

        const decodedToken = jwtDecode(token);

        if (decodedToken.senha_temporaria) {
          navigate('/minha-conta', { state: { message: 'Por favor, atualize sua senha padrão.' } });
        } else {
          const acesso = decodedToken.acesso || 'colaborador';
          navigate(acesso === 'admin' || acesso === 'gerente' ? '/dashboard' : '/meus-bonus');
        }

      } else {
        setError(result.message || 'Credenciais inválidas. Tente novamente.');
      }
    } catch (err) {
      console.error("Erro no login:", err);
      setError('Erro ao conectar ao servidor. Verifique sua conexão.');
    }
  };

  return (
    <div
      className="login-container"
      style={{
        position: 'relative',
        minHeight: '100vh',
        backgroundImage: `url(${LOGIN_BG})`,
        backgroundColor: 'var(--brand-bg-light, #f8fafc)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat'
      }}
    >
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '100vh' }}>
        <div className="card p-4" style={{ width: '400px', zIndex: 1, border: 'none', boxShadow: '0 10px 25px rgba(0,0,0,0.1)' }}>
          <div className="d-flex justify-content-between align-items-center mb-4">
            <h2 className="mb-0" style={{ fontWeight: 'bold', color: 'var(--brand-secondary, #1e293b)' }}>
              <i className="bi bi-box-arrow-in-right me-2" style={{ color: 'var(--brand-primary)' }}></i> Login
            </h2>
            {!isDemo && (
              <img
                src={CLIENT_LOGO}
                alt={CLIENT_NAME}
                style={{ maxWidth: '100px', height: 'auto', objectFit: 'contain' }}
                onError={(e) => { e.target.style.display = 'none'; }}
              />
            )}
          </div>

          <div className="mb-4 text-center">
             <small className="text-muted text-uppercase fw-bold" style={{ fontSize: '0.8rem', letterSpacing: '1.5px' }}>
                {CLIENT_NAME}
             </small>
          </div>

          {error && <Alert variant="danger" className="text-center py-2">{error}</Alert>}

          {isDemo ? (
            <div className="text-center">
              <div className="mb-4 p-3 rounded-2" style={{ backgroundColor: 'rgba(13, 202, 240, 0.08)', border: '1px solid rgba(13, 202, 240, 0.25)' }}>
                <div className="fw-bold mb-1" style={{ color: 'var(--brand-primary)', fontSize: '0.85rem' }}>
                  <i className="bi bi-eye me-1"></i> MODO DEMO
                </div>
                <div className="small text-muted">Dados fictícios. Nenhuma ação afeta um sistema real.</div>
              </div>
              <Button
                className="w-100 border-0 fw-bold shadow-sm"
                style={{ padding: '12px', fontSize: '1.1rem', backgroundColor: 'var(--brand-primary, #2563eb)' }}
                onClick={() => { login('token_demo_123'); navigate('/dashboard'); }}
              >
                <i className="bi bi-play-circle me-2"></i> Explorar Demo
              </Button>
            </div>
          ) : (
            <Form onSubmit={handleSubmit(onSubmit)}>
              <Form.Group className="mb-3">
                <Form.Label className="small fw-semibold text-muted">Usuário</Form.Label>
                <Form.Control
                  type="text"
                  className={errors.nome ? 'is-invalid border-danger' : ''}
                  {...register('nome', { required: 'O nome de usuário é obrigatório' })}
                  placeholder="Seu login"
                  autoComplete="username"
                  style={{ padding: '10px' }}
                />
                {errors.nome && <div className="invalid-feedback">{errors.nome.message}</div>}
              </Form.Group>

              <Form.Group className="mb-4">
                <Form.Label className="small fw-semibold text-muted">Senha</Form.Label>
                <div className="input-group">
                  <Form.Control
                    type={showPassword ? 'text' : 'password'}
                    className={errors.senha ? 'is-invalid border-danger' : ''}
                    {...register('senha', { required: 'A senha é obrigatória' })}
                    placeholder="Sua senha de acesso"
                    autoComplete="current-password"
                    style={{ padding: '10px' }}
                  />
                  <button
                    type="button"
                    className="input-group-text bg-white"
                    onClick={() => setShowPassword(!showPassword)}
                    aria-label="Mostrar ou ocultar senha"
                  >
                    <i className={`bi ${showPassword ? 'bi-eye-slash text-muted' : 'bi-eye'}`} style={{ color: showPassword ? undefined : 'var(--brand-primary)' }}></i>
                  </button>
                </div>
                {errors.senha && <div className="invalid-feedback d-block">{errors.senha.message}</div>}
              </Form.Group>

              <Button
                type="submit"
                className="w-100 border-0 fw-bold shadow-sm"
                style={{ padding: '12px', fontSize: '1.1rem', backgroundColor: 'var(--brand-primary, #2563eb)' }}
              >
                Acessar Painel
              </Button>
            </Form>
          )}
        </div>
      </div>
    </div>
  );
};

export default Login;